/**
 * Utility functions for handling admin URLs across different environments
 */

/**
 * Checks if the current location is running in an admin context
 */
export function isAdminContext(): boolean {
	const currentUrl = window.location.href;
	const adminUrl = import.meta.env.VITE_ADMIN_URL;

	// Check if current URL matches the configured admin URL or contains /admin path
	return currentUrl.includes(adminUrl) || window.location.pathname.includes('/admin');
}

/**
 * Gets the appropriate admin URL for the current environment using VITE_ADMIN_URL
 */
export function getAdminUrl(path = '', queryParams = ''): string {
	const currentUrl = window.location.href;
	const currentOrigin = window.location.origin;
	const adminUrl = import.meta.env.VITE_ADMIN_URL;

	// Ensure path starts with #/ for hash routing
	const adminPath = path.startsWith('#') ? path : `#/${path.replace(/^\//, '')}`;
	const params = queryParams ? `?${queryParams}` : '';

	// For secure production environment (secure.levitate.ai), redirect to configured admin URL
	if (currentUrl.includes('secure.levitate.ai')) {
		return `${adminUrl}${adminPath}${params}`;
	}

	// If we're already on the configured admin URL, use it directly
	if (currentUrl.includes(adminUrl)) {
		return `${adminUrl}${adminPath}${params}`;
	}

	// For local development or other environments, check if admin URL uses admin.html format
	if (adminUrl.includes('/admin.html')) {
		// Admin URL already includes admin.html, use it directly
		return `${adminUrl}${adminPath}${params}`;
	} else if (currentOrigin.includes('localhost')) {
		// Local development - use admin.html format
		return `${currentOrigin}/admin.html${adminPath}${params}`;
	} else {
		// Use the configured admin URL
		return `${adminUrl}${adminPath}${params}`;
	}
}

/**
 * Creates a campaign debug URL for the admin panel
 */
export function getCampaignDebugUrl(campaignId: string, accountId?: string): string {
	const path = `campaigns/${campaignId}/debug`;
	const queryParams = accountId ? `accountId=${accountId}` : '';
	return getAdminUrl(path, queryParams);
}
